import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gift, Package, Star, ArrowRight, CheckCircle2, ShoppingBag, PackageOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { mockRobots } from '../data/mock';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import FeatureCard from '../components/FeatureCard';
import CtaBanner from '../components/CtaBanner';
import IconBadge from '../components/IconBadge';
import Reveal from '../components/Reveal';
import CircuitDivider from '../components/CircuitDivider';
import { useBrandName } from '../context/SettingsContext';
import EditableText from '../components/EditableText';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL, BTN_PRIMARY, BTN_OUTLINE } from '../config/brand';

const BOUTIQUE_BENEFITS = [
  {
    icon: Gift,
    variant: 'purple',
    title: 'E-learning offert',
    description: 'Accès gratuit à notre plateforme de cours pendant 3 à 12 mois selon le robot',
  },
  {
    icon: Package,
    variant: 'orange',
    title: 'Livraison rapide',
    description: 'Expédition sous 48h sur toute la France métropolitaine',
  },
  {
    icon: Star,
    variant: 'gradient',
    title: 'Support expert',
    description: 'Nos formateurs vous accompagnent en cas de question',
  },
];

/** Normalise API (snake_case) ou mock (camelCase) vers le shape UI. */
function normalizeProduct(p) {
  return {
    id: p.id,
    name: p.name,
    price: p.price,
    ageRange: p.age_range ?? p.ageRange ?? '',
    description: p.description || '',
    image: p.image_url ?? p.image ?? '',
    features: Array.isArray(p.features) ? p.features : [],
    inStock: p.in_stock ?? p.inStock ?? true,
    bonusMonths: p.bonus_months ?? p.bonusMonths ?? 0,
  };
}

const Boutique = () => {
  const brandName = useBrandName();
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const [robots, setRobots] = useState(() => mockRobots.map(normalizeProduct));

  useEffect(() => {
    if (!BACKEND_URL) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/products`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          setRobots(data.map(normalizeProduct));
        }
      } catch {
        // fallback mockRobots
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const showProductsCta = !authLoading && isAuthenticated && isAdmin;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        eyebrow="Boutique"
        eyebrowKey="boutique.hero.eyebrow"
        title={`Robots & kits ${brandName}`}
        titleKey="boutique.hero.title"
        subtitle={`Achetez votre robot et bénéficiez d'un accès gratuit à la plateforme e-learning ${brandName} pour apprendre à la maison.`}
        subtitleKey="boutique.hero.subtitle"
      />

      <CircuitDivider className="mb-0" />

      <section className="py-14 sm:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Catalogue"
            eyebrowKey="boutique.catalog.eyebrow"
            title="Nos robots éducatifs"
            titleKey="boutique.catalog.title"
            subtitle="Des kits adaptés à chaque tranche d'âge, avec support pédagogique inclus. Contactez-nous pour commander."
            subtitleKey="boutique.catalog.subtitle"
            reveal
          />

          {showProductsCta && (
            <div className="mb-6 flex justify-center sm:justify-end">
              <Link
                to="/admin/products"
                className="inline-flex items-center gap-2 rounded-full border border-[rgba(58,0,224,0.2)] bg-[rgba(58,0,224,0.04)] px-4 py-2 text-sm text-[#3a00e0] hover:bg-[rgba(58,0,224,0.08)] transition-colors"
              >
                <ShoppingBag className="h-4 w-4 shrink-0" aria-hidden="true" />
                Gérer les produits
              </Link>
            </div>
          )}

          {robots.length === 0 ? (
            <div className="max-w-md mx-auto text-center py-12">
              <IconBadge icon={PackageOpen} variant="purple" size="lg" className="mx-auto mb-5" />
              <EditableText
                editKey="boutique.empty.title"
                defaultValue="Catalogue bientôt disponible"
                as="h3"
                className="text-xl font-semibold text-slate-900 mb-2"
              />
              <EditableText
                editKey="boutique.empty.text"
                defaultValue="Nos kits robotiques arrivent prochainement. Contactez-nous pour être conseillé en attendant."
                as="p"
                multiline
                className="text-slate-600 text-sm mb-6 leading-relaxed"
              />
              <Link to="/contact?subject=boutique">
                <Button className={BTN_PRIMARY}>
                  <EditableText editKey="boutique.empty.cta" defaultValue="Nous contacter" as="span" />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {robots.map((robot, i) => (
                <Reveal key={robot.id} animation="fade-up" delay={i * 60}>
                  <article className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col h-full">
                    <div className="relative h-48 sm:h-56 overflow-hidden bg-slate-50">
                      {robot.bonusMonths > 0 && (
                        <div className="absolute top-3 left-3 z-10">
                          <Badge className="bg-[#3a00e0]/90 text-white border-none">
                            <Gift className="h-3 w-3 mr-1" aria-hidden="true" />
                            +{robot.bonusMonths} mois e-learning
                          </Badge>
                        </div>
                      )}
                      <img
                        src={robot.image}
                        alt={robot.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>

                    <div className="p-5 flex flex-col flex-1 space-y-4">
                      {robot.ageRange && (
                        <Badge
                          variant="outline"
                          className="border-[rgba(58,0,224,0.25)] text-[#3a00e0] w-fit"
                        >
                          {robot.ageRange}
                        </Badge>
                      )}
                      <div>
                        <h3 className="text-slate-900 text-lg font-semibold mb-1 tracking-tight">
                          {robot.name}
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed">{robot.description}</p>
                      </div>

                      {robot.features.length > 0 && (
                        <ul className="space-y-2 flex-1">
                          {robot.features.map((feature, j) => (
                            <li key={j} className="flex items-center text-slate-600 text-xs">
                              <CheckCircle2
                                className="h-3 w-3 text-[#3a00e0] mr-1.5 shrink-0"
                                strokeWidth={1.75}
                                aria-hidden="true"
                              />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-200">
                        <div>
                          <div className="text-2xl font-bold text-slate-900">{robot.price}€</div>
                          {robot.inStock ? (
                            <div className="text-xs text-emerald-600 font-medium">En stock</div>
                          ) : (
                            <div className="text-xs text-red-600 font-medium">Rupture</div>
                          )}
                        </div>
                        {robot.inStock ? (
                          <Link to="/contact?subject=boutique" state={{ robotName: robot.name }}>
                            <Button className={BTN_PRIMARY}>Demander</Button>
                          </Link>
                        ) : (
                          <Button className={BTN_OUTLINE} disabled>
                            Indisponible
                          </Button>
                        )}
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Nos engagements"
            eyebrowKey="boutique.why.eyebrow"
            title={`Pourquoi acheter chez ${brandName} ?`}
            titleKey="boutique.why.title"
            reveal
          />

          <div className="grid md:grid-cols-3 gap-6">
            {BOUTIQUE_BENEFITS.map((item, i) => (
              <Reveal key={item.title} animation="fade-up" delay={i * 80}>
                <FeatureCard
                  {...item}
                  size="md"
                  titleKey={`boutique.why.${i + 1}.title`}
                  descriptionKey={`boutique.why.${i + 1}.text`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        title="Besoin de conseils ?"
        titleKey="boutique.cta.title"
        subtitle="Pas sûr du robot adapté à votre enfant ? Contactez-nous pour des conseils personnalisés."
        subtitleKey="boutique.cta.subtitle"
      >
        <Link to="/contact?subject=boutique">
          <Button size="lg" className="bg-white text-[#3a00e0] hover:bg-[rgba(58,0,224,0.06)]">
            <EditableText editKey="boutique.cta.button" defaultValue="Contactez-nous" as="span" />
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
      </CtaBanner>

      <Footer />
    </div>
  );
};

export default Boutique;
